// The compare page's state in the address: which products are compared, in
// which order, against which reference, and the two switches. What the page
// shows is read from the query string and written back to it, so that a
// copied address or a reload shows the same comparison. The module is pure:
// it knows nothing of the router or of React.
//
//   /compare?product=<id>&product=<id>&ref=<id>&diff=1&sources=1
//
// - `product` is repeated, once per product; the order of the parameters is
//   the order of the columns. A repeated id is read once, at its first
//   place, and more than six are cut to the first six.
// - `ref` names the reference. It is kept only when it names one of the
//   products; else the first product is the reference, and `ref` is not
//   written.
// - `diff` ("differences only") and `sources` ("show the sources") are
//   written as "1", and only when on.

export const COMPARE_PATH = "/compare";

// The most products one comparison, and so the tray, holds
export const MAX_COMPARED = 6;

const PRODUCT_PARAM = "product";
const REFERENCE_PARAM = "ref";
const DIFFERENCES_PARAM = "diff";
const SOURCES_PARAM = "sources";

const OWN_PARAMS = [
  PRODUCT_PARAM,
  REFERENCE_PARAM,
  DIFFERENCES_PARAM,
  SOURCES_PARAM,
];

const ON = "1";

export interface CompareState {
  // The ids of the compared products, in the order of the columns: no
  // repeats, six at most
  products: string[];
  // The id of the product the others are set against: always one of
  // `products`, and null only while there is none
  reference: string | null;
  // Whether the rows that are the same for every product are hidden
  differencesOnly: boolean;
  // Whether every cell lists its sources
  showSources: boolean;
}

export const EMPTY_COMPARE_STATE: CompareState = {
  products: [],
  reference: null,
  differencesOnly: false,
  showSources: false,
};

// The ids as a comparison holds them: no empty one, each once, six at most
const boundProducts = (ids: string[]): string[] =>
  Array.from(new Set(ids.filter(Boolean))).slice(0, MAX_COMPARED);

// The reference of a set: the one asked for when it is compared, else the
// first product
const referenceOf = (
  products: string[],
  asked: string | null,
): string | null =>
  asked !== null && products.includes(asked) ? asked : (products[0] ?? null);

export const decodeCompareState = (params: URLSearchParams): CompareState => {
  const products = boundProducts(params.getAll(PRODUCT_PARAM));
  return {
    products,
    reference: referenceOf(products, params.get(REFERENCE_PARAM)),
    differencesOnly: params.get(DIFFERENCES_PARAM) === ON,
    showSources: params.get(SOURCES_PARAM) === ON,
  };
};

/**
 * The state as query parameters.
 *
 * @param state - The state to write
 * @param current - The address the page is at; the parameters the state
 *   does not own (the detail overlay's) are kept, after the state's own
 * @returns New parameters; `current` is not changed
 */
export const encodeCompareState = (
  state: CompareState,
  current?: URLSearchParams,
): URLSearchParams => {
  const params = new URLSearchParams();
  const products = boundProducts(state.products);
  for (const id of products) {
    params.append(PRODUCT_PARAM, id);
  }
  const reference = referenceOf(products, state.reference);
  if (reference !== null && reference !== products[0]) {
    params.set(REFERENCE_PARAM, reference);
  }
  if (state.differencesOnly) {
    params.set(DIFFERENCES_PARAM, ON);
  }
  if (state.showSources) {
    params.set(SOURCES_PARAM, ON);
  }
  current?.forEach((value, name) => {
    if (!OWN_PARAMS.includes(name)) {
      params.append(name, value);
    }
  });
  return params;
};

/**
 * The address of the comparison of some products, for a link to the page.
 *
 * @param productIds - The products, in the order of the columns; repeats
 *   are dropped and more than six are cut to the first six
 * @returns The path with its query; the bare path for no product
 */
export const compareAddress = (productIds: string[]): string => {
  const query = encodeCompareState({
    ...EMPTY_COMPARE_STATE,
    products: productIds,
  }).toString();
  return query ? `${COMPARE_PATH}?${query}` : COMPARE_PATH;
};

// The state with a product added as the last column. A product already
// compared, and a seventh, change nothing: the same state is returned.
export const addProduct = (state: CompareState, id: string): CompareState => {
  if (
    !id ||
    state.products.includes(id) ||
    state.products.length >= MAX_COMPARED
  ) {
    return state;
  }
  const products = [...state.products, id];
  return {
    ...state,
    products,
    reference: referenceOf(products, state.reference),
  };
};

// The state without a product. When it was the reference, the first of the
// products left is.
export const removeProduct = (
  state: CompareState,
  id: string,
): CompareState => {
  if (!state.products.includes(id)) {
    return state;
  }
  const products = state.products.filter((product) => product !== id);
  return {
    ...state,
    products,
    reference: referenceOf(
      products,
      state.reference === id ? null : state.reference,
    ),
  };
};

// The state with another reference; an id that is not compared changes
// nothing
export const setReference = (state: CompareState, id: string): CompareState =>
  state.products.includes(id) ? { ...state, reference: id } : state;
